/**
 * Platform Operations screen (SASADM-0127). Home for platform-wide (not
 * per-tenant) actions. The default-hostname backfill moved here unmodified
 * from the tenant Whitelabel screen (SASADM-0118) — same walk, same
 * BACKFILL_MAX_PAGES cap, same result alerts. This page also owns the
 * platform routing deploy directly: there is no current tenant here, so
 * there is no per-tenant readiness to gate the deploy button on (unlike
 * TenantHostnames.tsx's `useRoutingDeploy`).
 */
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import { isDeployProblem } from '@/api/routingDeploy';
import type {
  DeployRoutingResponse,
  HostnameBackfillSkippedTenant,
  HostnameBackfillSummary,
  RoutingDeployOutcome,
} from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { HeldBackConflictNotice } from '@/components/HeldBackConflictNotice';
import { RoutingDeployBanner } from '@/components/RoutingDeployBanner';

// SASADM-0118 — hard cap on the auto-resume walk (server default page = 200 tenants,
// so 20 pages covers 4000). Reaching the cap is reported as an unfinished run with a
// Continue affordance — never as success.
const BACKFILL_MAX_PAGES = 20;

export function PlatformOperations() {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState<DeployRoutingResponse | null>(null);
  // SASADM-0170 (SASATH-0387) — a deploy whose routing deploy was REJECTED / FAILED (SASADM-0176:
  // or PARTIAL): the banner renders instead of the result.
  const [deployFailure, setDeployFailure] = useState<RoutingDeployOutcome | null>(null);
  // SASADM-0176 (SASATH-0392) — a 409 CADDY_HELD_BACK_HOST_CONFLICT refusal.
  const [heldBackConflict, setHeldBackConflict] = useState<ApiError | null>(null);

  async function handleDeployPlatform() {
    setDeploying(true);
    setHeldBackConflict(null);
    try {
      const res = await apiAdmin.routing.deployRouting();
      if (isDeployProblem(res.routingDeploy)) {
        setDeployResult(null);
        setDeployFailure(res.routingDeploy);
        return;
      }
      setDeployFailure(null);
      setDeployResult(res);
      snack.success(t('routing.deploy.doneGlobalSnack', 'Routing deployed.'));
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'CADDY_HELD_BACK_HOST_CONFLICT') {
          setDeployResult(null);
          setDeployFailure(null);
          setHeldBackConflict(err);
        } else if (err.code === 'CADDY_CONFIG_INVALID' || err.code === 'CADDY_RELOAD_FAILED') {
          snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        } else if (err.status === 403) {
          snack.error(
            t('errors.insufficientPermissions', "You don't have permission for this action."),
          );
        } else {
          snack.error(t(`errors.codes.${err.code}`, err.code));
        }
      }
    } finally {
      setDeploying(false);
    }
  }

  const { backfillButton, backfillConfirm, backfillFeedback } = useHostnameBackfill({
    deployNow: handleDeployPlatform,
  });

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.operations', 'Operations') },
        ]}
      />
      <PageHeader
        title={t('operations.title', 'Platform Operations')}
        subtitle={t('operations.intro', 'Actions that affect every tenant in this environment.')}
      />
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t(
              'operations.backfillSection',
              'Provision missing default hostnames (login, auth, brand, asset) for every tenant in this environment.',
            )}
          </Typography>
          {backfillButton}
          {backfillFeedback}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {t(
              'operations.deploySection',
              'Regenerate and reload Caddy routing for every tenant from the database. This is idempotent and safe to run at any time.',
            )}
          </Typography>
          <Button
            variant="contained"
            onClick={() => void handleDeployPlatform()}
            disabled={deploying}
            startIcon={deploying ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('routing.deploy.button', 'Deploy Platform Routing')}
          </Button>
          {deployFailure && (
            <Box sx={{ mt: 1.5 }}>
              <RoutingDeployBanner outcome={deployFailure} onClose={() => setDeployFailure(null)} />
            </Box>
          )}
          {heldBackConflict && (
            <Box sx={{ mt: 1.5 }}>
              <HeldBackConflictNotice
                err={heldBackConflict}
                onClose={() => setHeldBackConflict(null)}
              />
            </Box>
          )}
          {deployResult && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              {t(
                'routing.deploy.doneGlobal',
                'Routing deployed — {{tenants}} tenants, {{written}} fragments written, {{removed}} removed.',
                {
                  tenants: deployResult.tenants,
                  written: deployResult.fragmentsWritten,
                  removed: deployResult.filesRemoved,
                },
              )}{' '}
              {deployResult.reloaded
                ? t('routing.deploy.reloaded', 'Caddy reloaded.')
                : t('routing.deploy.reloadSkipped', 'Caddy reload skipped.')}
            </Alert>
          )}
        </Paper>
      </Stack>
      {backfillConfirm}
    </Box>
  );
}

// SASADM-0118 — accumulate a resumed page onto the running total. Counters sum;
// `skippedTenants` concatenates; `notConfigured` / `truncated` / `nextTenantId` always
// take the LATEST page's value, because they describe where the walk now stands.
function mergeBackfill(
  acc: HostnameBackfillSummary,
  next: HostnameBackfillSummary,
): HostnameBackfillSummary {
  return {
    tenantsProcessed: acc.tenantsProcessed + next.tenantsProcessed,
    tenantsSkipped: acc.tenantsSkipped + next.tenantsSkipped,
    rowsCreated: acc.rowsCreated + next.rowsCreated,
    rowsSkipped: acc.rowsSkipped + next.rowsSkipped,
    notConfigured: next.notConfigured,
    truncated: next.truncated,
    nextTenantId: next.nextTenantId,
    skippedTenants: [...(acc.skippedTenants ?? []), ...(next.skippedTenants ?? [])],
  };
}

// SASADM-0118 — "Backfill Hostnames (All Tenants)": runs the platform-wide
// default-hostname backfill (saas-auth SASATH-0186 / SASATH-0247). `summary` holds
// the ACCUMULATED result across the walk (see runBackfill); `stoppedEarly` is true
// when an ApiError ended the walk after at least one page had already succeeded.
// SASADM-0127 — moved here from TenantHostnames.tsx. On this page the deploy is
// never readiness-gated, so this hook takes only `deployNow` (no `deployReady` /
// `deployDisabledReason`).
function useHostnameBackfill({ deployNow }: { deployNow: () => Promise<void> }) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState<HostnameBackfillSummary | null>(null);
  const [stoppedEarly, setStoppedEarly] = useState(false);

  // Walks the cursor to completion, accumulating counters. `startAfter` is undefined
  // for a fresh run and `nextTenantId` when resuming; `base` is the summary to
  // accumulate onto (null for a fresh run) so Continue keeps the earlier totals.
  async function runBackfill(startAfter: number | undefined, base: HostnameBackfillSummary | null) {
    setRunning(true);
    setStoppedEarly(false);
    let acc: HostnameBackfillSummary | null = base;
    let cursor = startAfter;
    try {
      for (let page = 0; page < BACKFILL_MAX_PAGES; page += 1) {
        const res = await apiAdmin.hostnames.backfillDefaultHostnames(
          cursor === undefined ? undefined : { afterTenantId: cursor },
        );
        // B3 — nothing ran at all; report the configuration problem verbatim and stop.
        if (res.notConfigured) {
          acc = res;
          break;
        }
        acc = acc === null ? res : mergeBackfill(acc, res);
        if (!res.truncated || res.nextTenantId == null) break;
        cursor = res.nextTenantId;
      }
      setSummary(acc);
      // B2 — a truncated accumulation is NOT success; only a completed walk gets the
      // success snackbar.
      if (acc && !acc.notConfigured && !acc.truncated) {
        snack.success(t('hostnames.backfill.doneSnack', 'Default hostnames backfilled.'));
      }
    } catch (err) {
      if (acc !== null) {
        setSummary(acc);
        setStoppedEarly(true);
      }
      if (err instanceof ApiError) {
        if (err.status === 403) {
          snack.error(
            t('errors.insufficientPermissions', "You don't have permission for this action."),
          );
        } else {
          snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        }
      }
    } finally {
      setRunning(false);
    }
  }

  const backfillButton = (
    <Button
      variant="outlined"
      onClick={() => setConfirmOpen(true)}
      disabled={running}
      startIcon={running ? <CircularProgress size={16} color="inherit" /> : null}
    >
      {t('hostnames.backfill.button', 'Backfill Hostnames (All Tenants)')}
    </Button>
  );

  const backfillConfirm = (
    <ConfirmDialog
      open={confirmOpen}
      severity="warning"
      title={t('hostnames.backfill.confirmTitle', 'Backfill default hostnames?')}
      body={
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t(
              'hostnames.backfill.confirmBody',
              'This provisions any missing default hostnames (login, auth, brand, asset) for ALL tenants in this environment. It is idempotent and additive — existing hostnames are left exactly as they are.',
            )}
          </Typography>
          <Typography variant="body2">
            {t(
              'hostnames.backfill.confirmSessions',
              'When a tenant gains a login host, its session cookie moves to the tenant apex, so users of affected tenants are signed out once — including you, if you hold a session on one.',
            )}
          </Typography>
        </>
      }
      confirmLabel={t('hostnames.backfill.confirmAction', 'Run backfill')}
      busy={running}
      onConfirm={() => {
        setConfirmOpen(false);
        void runBackfill(undefined, null);
      }}
      onCancel={() => setConfirmOpen(false)}
    />
  );

  const backfillFeedback = summary ? (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {summary.notConfigured ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {t(
            'hostnames.backfill.notConfigured',
            'Nothing ran — the platform base domain is not configured in this environment. No hostnames were created. This is an environment configuration problem: set the tenant base domain, then run the backfill again.',
          )}
        </Alert>
      ) : (
        <>
          {stoppedEarly && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {t(
                'hostnames.backfill.stoppedEarly',
                'The run stopped early after an error. The counts below cover only the tenants processed before it stopped.',
              )}
            </Alert>
          )}
          {summary.truncated && (
            <Alert
              severity="warning"
              sx={{ mt: 1.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={running || summary.nextTenantId == null}
                  onClick={() => void runBackfill(summary.nextTenantId ?? undefined, summary)}
                >
                  {t('hostnames.backfill.continue', 'Continue')}
                </Button>
              }
            >
              {t(
                'hostnames.backfill.truncated',
                'NOT finished — tenants remain unprovisioned. {{processed}} tenants processed and {{created}} hostnames created so far. Choose Continue to resume.',
                { processed: summary.tenantsProcessed, created: summary.rowsCreated },
              )}
            </Alert>
          )}
          {!summary.truncated && !stoppedEarly && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              {t(
                'hostnames.backfill.done',
                'Backfill complete — {{processed}} tenants processed, {{created}} hostnames created, {{existing}} already present, {{skipped}} tenants skipped.',
                {
                  processed: summary.tenantsProcessed,
                  created: summary.rowsCreated,
                  existing: summary.rowsSkipped,
                  skipped: summary.tenantsSkipped,
                },
              )}
            </Alert>
          )}
          {(summary.skippedTenants ?? []).length > 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ display: 'block' }}>
                {t(
                  'hostnames.backfill.skippedIntro',
                  'Skipped, with reasons — this is normal, not a failure. A tenant with no presence on the platform base domain is deliberately skipped, because minting platform hosts for it would break its session cookie:',
                )}
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                {(summary.skippedTenants ?? []).map((skipped: HostnameBackfillSkippedTenant) => (
                  <Typography component="li" key={skipped.tenantId} variant="caption">
                    {skipped.slug || `#${skipped.tenantId}`} — {skipped.reason}
                    {skipped.message ? ` (${skipped.message})` : ''}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}
          {summary.rowsCreated > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 1.5 }}
              action={
                <Button color="inherit" size="small" onClick={() => void deployNow()}>
                  {t('routing.deploy.button', 'Deploy Platform Routing')}
                </Button>
              }
            >
              {t(
                'hostnames.backfill.deployRequired',
                'Not serving yet — {{created}} hostname rows were created in the database, but routing has not been deployed. Until you deploy platform routing, nothing serves these hosts.',
                { created: summary.rowsCreated },
              )}
            </Alert>
          )}
        </>
      )}
    </Paper>
  ) : null;

  return { backfillButton, backfillConfirm, backfillFeedback };
}
